<template>
  <div>
    <notification-modal :is-error-modal="false" v-model:is-open="showModal">
      <template #title-content>
        Report Queued!
      </template>
      Sonicu has queued your report! It should land in your email inbox shortly.
      <template #button-content>Sounds good!</template>
    </notification-modal>

    <div class="py-4 grid grid-cols-1 md:grid-cols-4 flex flex-row">
      <div class="mx-4 mb-2 flex flex-row md:col-span-3 items-center justify-start">

        <div class="flex flex-col justify-around w-full">
          <!-- Search -->
          <div class="flex flex-col justify-between md:flex-row md:items-center">
            <div class="w-full md:w-1/3">
              <floating-input
                  label="Search"
                  v-model:value="search"
              ></floating-input>
            </div>

            <div class="mb-6 w-full md:w-1/4">
              <date-time-picker   v-if="showDateTimePicker"
                                  id="month"
                                  class="w-full"
                                  v-model="month"
                                  :format="format"
                                  :view-mode="viewMode"
                                  :min-date="minDate"
                                  :max-date="maxDate"
              ></date-time-picker>
              <loading v-else width="w-16"></loading>
            </div>
<!--            Radio select buttons-->
            <div class="mb-6 w-full md:w-1/3">
              <span class="relative z-0 inline-flex shadow-sm rounded-md w-full">
                <button type="button"
                        @click="startOfMonth = true"
                        :class="startOfMonth ? 'bg-blue-100' : 'bg-white'"
                        class="relative w-1/2 text-center inline-flex items-center px-4 py-3 rounded-l-md border border-gray-300 text-md leading-5 font-medium text-gray-700 hover:text-gray-500 focus:z-10 focus:outline-none focus:border-blue-300 focus:ring-blue-200 active:bg-gray-100 active:text-gray-700">
                  Start of Month
                </button>
                <button type="button"
                        @click="startOfMonth = false"
                        :class="startOfMonth === false ? 'bg-blue-100' : 'bg-white'"
                        class="-ml-px w-1/2 text-center relative inline-flex items-center px-4 py-3 rounded-r-md border border-gray-300 text-md leading-5 font-medium text-gray-700 hover:text-gray-500 focus:z-10 focus:outline-none focus:border-blue-300 focus:ring-blue-200 active:bg-gray-100 active:text-gray-700">
                  End of Month
                </button>
              </span>

            </div>
<!--            TODO: Remove offline/threshold radios-->
          </div>
        </div>
      </div>

      <div class="flex flex-row justify-start md:justify-end">
<!--        TODO: Remove items-per-page-select code -->
        <span class="rounded-md w-full md:w-auto px-4 -mt-3 md:mt-0">
          <button type="button"
                  @click="postMultiplePointReport"
                  :disabled="data.length === 0"
                  :class="data.length > 0 ? 'bg-blue-600 hover:bg-blue-500 focus:outline-none focus:border-blue-700 focus:ring-blue-200 active:bg-blue-700' : 'bg-gray-600 hover:bg-gray-500 focus:outline-none focus:border-gray-700 focus:ring-gray-200 active:bg-gray-700'"
                  class="w-full md:inline-flex items-center px-4 py-3 border border-transparent text-base leading-6 font-medium rounded-md text-white">
            <!-- Heroicon name: mail -->
            <span class="lnr icon-envelope pr-4"></span>
            Get All Data
          </button>
        </span>
      </div>
    </div>

    <div class="bg-white shadow overflow-hidden sm:rounded-md border">
      <ul class="m-0">
        <!-- Header Row -->
        <li>
          <div class="block flex justify-around text-gray-500 bg-gray-50 font-semibold grid grid-cols-2 sm:grid-cols-2 md:grid-cols-5 lg:grid-cols-5
                        hover:bg-gray-50 focus:outline-none focus:bg-gray-50 border">
            <div class="cursor-pointer inline-block grow flex items-center px-4 py-4 sm:px-6 "
                 @click="updateSort('name')">
              <span class="pr-2">Point Info</span>
              <span v-show="sortBy ==='name'" :class="sortDesc ? iconSortDesc : iconSortAsc"></span>
            </div>
            <div class="cursor-default hidden md:inline-block grow px-4 py-4 sm:px-6 "
            >
              <span class="pr-2">Location</span>
            </div>
            <div class="cursor-pointer inline-block grow px-4 py-4 sm:px-6 "
            >
              <span class="hidden md:block pr-2">Alarms</span>
              <span class="md:hidden pr-2">Summary</span>
            </div>
            <div class="cursor-default hidden md:inline-block grow px-4 py-4 sm:px-6 "
            >
              <span class="pr-2">Calibration</span>
            </div>
            <div class="cursor-pointer hidden md:inline-block grow px-4 py-4 sm:px-6 "
            >
              <span class="pr-2">Get Data</span>
            </div>
          </div>
        </li>

        <!-- Normal Row -->
        <li v-if="loading">
          <div class="flex flex-row items-center justify-center py-4">
            <span :class="iconLoading"></span>
            <span class="text-grey-500 text-base font-medium">Loading</span>
          </div>
        </li>
        <li v-else-if="data.length > 0" v-for="(row, rowIndex) in data"
            :class="{'bg-gray-100' : rowIndex % 2 === 1}">
          <!-- Configure the cols by number of columns provided by default w/ lg being max -->
          <!-- Mobile Version -->
          <div class="block md:hidden
                        hover:bg-gray-50">
            <div class="grid grid-cols-2 sm:grid-cols-2 md:grid-cols-5 lg:grid-cols-5">
              <!-- Cell -->
              <!-- Point Info -->
              <div class="inline-block grow px-4 py-4 sm:px-6">
                <div class="flex flex-row items-center h-full">
                  <div class="flex flex-col content-start ml-4">
                    <span class="font-semibold mb-2 mt-1">
                      {{ row.name }}
                    </span>
                    <div class="text-sm leading-5 text-gray-900 mb-2">
                      {{ row.serial }}
                    </div>
                    <div class="text-sm leading-5 text-gray-900 mb-2">
                      <span class="lnr icon-pushpin pr-2"></span>
                      {{ row.vfc_pin }}
                    </div>
                  </div>
                </div>
              </div>

              <!-- Get Data -->
              <div class="inline-block grow px-4 py-4 sm:px-6 flex flex-col justify-center">
                <button type="button"
                        @click="postSinglePointReport(row)"
                        class="inline-flex items-center px-4 py-2 border border-transparent text-base leading-6 font-medium rounded-md text-white bg-blue-600 hover:bg-blue-500 focus:outline-none focus:border-blue-700 focus:ring-blue-200 active:bg-blue-700">
                  <!-- Heroicon name: mail -->
                  <span class="lnr icon-license pr-4"></span>
                  Get Data
                </button>
              </div>

            </div>
          </div>

          <!-- Desktop Version -->
          <div class="hidden md:block hover:bg-gray-50">
            <div class="grid grid-cols-2 sm:grid-cols-2 md:grid-cols-5 lg:grid-cols-5">
              <!-- Cell -->
              <!-- point name, zone, & Area -->
              <div class="inline-block grow px-4 py-4 sm:px-6">
                <div class="flex flex-row items-center h-full">
                  <div class="flex flex-col content-start ml-4">
                    <span class="font-semibold mb-2 mt-1">
                      {{ row.name }}
                    </span>
                    <div class="text-sm leading-5 text-gray-900 mb-2">
                      <span class="lnr icon-barcode pr-2"></span>
                      {{ row.serial }}
                    </div>
                    <div class="text-sm leading-5 text-gray-900 mb-2">
                      <span class="lnr icon-pushpin pr-2"></span>
                      {{ row.vfc_pin }}
                    </div>
                  </div>
                </div>
              </div>

              <!-- Company, zone, & Area -->
              <div class="inline-block grow px-4 py-4 sm:px-6">
                <div class="flex flex-row items-center h-full">
                  <div class="flex flex-col content-start ml-4">
                    <span class="font-semibold mb-2 mt-1">
                      {{ row.company }}
                    </span>
                    <div class="text-sm leading-5 text-gray-500 mb-2">
                      <span class="lnr icon-home pr-2"></span>
                      {{ row.area }}
                    </div>
                    <div class="text-sm leading-5 text-gray-500 mb-2">
                      <span class="lnr icon-road-sign pr-2"></span>
                      {{ row.zone }}
                    </div>
                  </div>
                </div>
              </div>

              <!-- Alarms -->
              <div class="inline-block grow px-4 py-4 sm:px-6">
                <div class="">
                  <span v-if="row.alarm_count > 0"
                        class="px-2 py-1 mb-2 inline-flex text-sm leading-5 font-semibold rounded-full bg-red-100 text-red-800">
                    {{ row.alarm_count }} alarm<template v-if="row.alarm_count !== 1">s</template> last 30 days
                  </span>
                  <span v-else class="px-2 py-1 mb-2 inline-flex text-sm leading-5 font-semibold rounded-full bg-green-100 text-green-800">
                    {{ row.alarm_count }} alarm<template v-if="row.alarm_count !== 1">s</template> last 30 days
                  </span>
                  <div class="text-sm leading-5 text-gray-500 mb-2">
                    <span class="lnr icon-graph pr-2"></span>
                    <span class="border-b border-dotted text-wrap">
                      {{ row.alarm_profile_name }}
                    </span>
                  </div>
                </div>
              </div>

              <!-- Calibration -->
              <div class="hidden md:inline-block grow px-4 py-4 sm:px-6">
                <div class="">
                  <span v-if="row.in_calibration"
                        class="px-2 py-1 inline-flex text-sm leading-5 font-semibold rounded-full bg-green-100 text-green-800">
                    In Calibration
                  </span>
                  <span v-else-if="row.nearing_calibration"
                        class="px-2 py-1 inline-flex text-sm leading-5 font-semibold rounded-full bg-yellow-100 text-yellow-800">
                    Nearing Calibration
                  </span>
                  <span v-else
                    class="px-2 py-1 inline-flex text-sm leading-5 font-semibold rounded-full bg-red-100 text-red-800">
                  Out of Calibration
                </span>
                  <div v-if="row.probe_serial" class="text-sm leading-5 text-gray-500 mt-2">
                    <span class="lnr icon-thermometer pr-2"></span>
                    {{ row.probe_serial }}
                  </div>
                  <div class="text-sm leading-5 text-gray-500 mt-2 mb-2">
                    <span class="lnr icon-calendar-empty pr-2"></span>
                    {{ row.calibration_date }}
                  </div>
                </div>
              </div>

              <!-- Get Data -->
              <div class="hidden md:inline-block grow px-4 py-4 sm:px-6">
                <div class="flex flex-col justify-center">
                  <span class="inline-flex rounded-md">
                    <button type="button"
                            @click="postSinglePointReport(row)"
                            class="inline-flex items-center px-4 py-2 border border-transparent text-base leading-6 font-medium rounded-md text-white bg-blue-600 hover:bg-blue-500 focus:outline-none focus:border-blue-700 focus:ring-blue-200 active:bg-blue-700">
                      <!-- Heroicon name: mail -->
                      <span class="lnr icon-license pr-4"></span>
                      Get Data
                    </button>
                  </span>
                </div>
              </div>
            </div>
          </div>
        </li>
        <li v-else>
          <div v-if="search.length > 0" class="flex flex-row items-center justify-center py-4">
            <span :class="iconError"></span>
            <span class="text-grey-500 text-base font-medium">No Points Found</span>
          </div>
          <div v-else class="flex flex-row items-center justify-center py-4">
            <span class="lnr icon-magnifier pr-2"></span>
            <span class="text-grey-500 text-base font-medium">Search for a VFC PIN</span>
          </div>
        </li>
      </ul>
      <pagination v-if="!loading"
                  :num-pages="pageInfo.pages"
                  v-model:page="page"
                  @update:page="updatePage($event)"
      ></pagination>
    </div>

  </div>
</template>

<script>
import {SonicuCoreMixin} from "@/mixins/SonicuCoreMixin";
import {iconNamesMixin} from "@/mixins/IconNamesMixin";
import axios from "axios"
import {isNil} from "lodash";
import Pagination from "@/components/Pagination";
import ItemsPerPageSelect from "@/components/ItemsPerPageSelect";
import InlineTabHeader from "@/components/elements/InlineTabHeader";
import FloatingInput from "@/components/elements/FloatingInput";
import DateTimePicker from "@/components/Reports/DateTimePicker";
import JsonExcel from './JsonExcel'
import Loading from "@/components/elements/loading";
import NotificationModal from "@/components/elements/NotificationModal";

const SEARCH_DELAY = 250

export default {
  name: "OversightTable",
  mixins: [SonicuCoreMixin, iconNamesMixin],
  components: {
    NotificationModal,
    Loading,
    DateTimePicker, FloatingInput, ItemsPerPageSelect, Pagination,
    InlineTabHeader, 'download-excel': JsonExcel
  },
  props: {
    minDate: Date,
    maxDate: Date,
    startOfMonthProp: Boolean,
  },
  data() {
    return {
      loading: false,
      data: [],
      modalAlarm: null,
      showModal: false,
      itemsPerPage: '10',
      search: '',
      pageInfo: {
        pages: 1,
        from: 1,
        to: 1,
        total: 1,
      },
      page: 1,
      sortBy: 'reading',
      sortDesc: true,
      loadFunction: null,
      startTime: null,
      endTime: null,
      startOfMonth: this.startOfMonthProp,
      format: 'MMMM YYYY',
      viewMode: 'months',
      month: this.maxDate,
      showDateTimePicker: false,
    }
  },
  created() {
    this.loadFunction = _.debounce(() => {
      this.loading = true
      let params = {
        page: this.page,
        search: this.search,
        sortBy: this.sortBy,
        sortDesc: this.sortDesc,
        startOfMonth: this.startOfMonth,
        month: this.month !== null ? `${this.month.toISOString()}` : null,
      }
      this.storeUrl(params)

      axios.get(this.company.buildCompanyApiInternalRoute('oversight/'), {
        params: params
      }).then(response => {
        this.loading = false
        this.data = response.data.data !== undefined ? response.data.data : []
        if (response.data.meta) {
          this.pageInfo.pages = response.data.meta.last_page
          this.pageInfo.from = response.data.meta.from
          this.pageInfo.to = response.data.meta.to
          this.pageInfo.total = response.data.meta.total
        } else {
          this.pageInfo = {
            pages: 1,
            from: 0,
            to: 0,
            total: 0,
          }
        }
        if (this.page > this.pageInfo.pages) {
          this.page = this.pageInfo.pages
          this.loadData()
        }

      }).catch(error => {
        this.loading = false
        this.data = []
      })
    }, SEARCH_DELAY)
  },
  mounted() {
    this.readUrl()
    window.addEventListener('popstate', this.handleBackButton)
    this.loadData()
  },
  methods: {
    loadData() {
      // this.data = []
      this.loadFunction()
    },
    updatePage(page) {
      this.page = page
      this.loadData()
    },
    updateSort(by) {
      if (this.sortBy === by) {
        this.sortBy = this.sortDesc ? '' : this.sortBy
        this.sortDesc = !this.sortDesc
      } else {
        this.sortBy = by
        this.sortDesc = false
      }
      this.loadData()
    },
    handleBackButton() {
      this.readUrl()
      this.loadData()
    },
    readUrl() {
      let url = window.location.href.split('?')
      let data = []
      if (url.length <= 1) {
        this.showDateTimePicker = true;
        return data
      }

      url = url[1].split('&')
      url.forEach(param => {
        param = param.split('=')
        data[param[0]] = param[1]
      })

      this.page = !isNil(data['page']) ? Number(data['page']) : this.page
      this.search = !isNil(data['search']) ? data['search'] : this.search
      this.startOfMonth = !isNil(data['startOfMonth']) ? data['startOfMonth'] === 'true' : this.startOfMonth
      this.sortBy = data['sortBy']
      this.sortDesc = data['sortDesc']
      this.month = 'month' in data && data['month'] !== "null" ? new Date(data['month']) : null
      this.showDateTimePicker = true
    },
    storeUrl(data) {
      let url = ''

      Object.keys(data).forEach(key => {
        url += `${key}=${data[key]}&`
      })
      window.history.pushState(window.location.hostname, "Table", this.company.buildCompanyRoute('oversight?') + url)
    },
    isNil,
    postSinglePointReport(row){
      axios.post(this.company.buildCompanyApiInternalRoute('oversight'), {
        'type': 'single',
        'data': row,
        'startOfMonth': this.startOfMonth,
        'month': this.month,
      }).then(response => {
        this.showModal = true;
      });
    },
    postMultiplePointReport(){
      axios.post(this.company.buildCompanyApiInternalRoute('oversight'), {
        'type': 'multiple',
        'data': this.data,
        'startOfMonth': this.startOfMonth,
        'month': this.month,
      }).then(response => {
        this.showModal = true;
      });
    },
  },
  watch: {
    search() {
      this.loadData()
    },
    month() {
      if (_.isNil(this.month) || _.isNaN(this.month.getTime())) {
        this.month = this.maxDate
      }
      this.storeUrl({
        search: this.search,
        sortBy: this.sortBy,
        sortDesc: this.sortDesc,
        startOfMonth: this.startOfMonth,
        month: this.month !== null ? `${this.month.toISOString()}` : null,
      })
    },
    startOfMonth() {
      this.storeUrl({
        search: this.search,
        sortBy: this.sortBy,
        sortDesc: this.sortDesc,
        startOfMonth: this.startOfMonth,
        month: this.month !== null ? `${this.month.toISOString()}` : null,
      })
    },
  },
  computed: {},
  unmounted() {
    window.removeEventListener('popstate', this.handleBackButton)
  }
}
</script>

<style scoped>

</style>
